import type { AppLang } from "@/locale/appLang";
import { BCP47_BY_LANG } from "@/locale/appLang";
import { pickOfferSite } from "@/locale/content/offerSite";
import { canonicalUrl, OG_IMAGE_URL, pickPageSeo, SEO_TRUST, SITE_ORIGIN } from "@/lib/seoConstants";
import { localizedPath } from "@/locale/routes";

export { breadcrumbSchema as buildBreadcrumbListSchema } from "@/lib/seoBreadcrumb";

const LOGO_URL = `${SITE_ORIGIN}/favicon.png`;
const ORG_ID = `${SITE_ORIGIN}/#organization`;

function orgProviderRef() {
  return {
    "@type": "Organization" as const,
    "@id": ORG_ID,
    name: "IT-24.pro",
    url: `${SITE_ORIGIN}/`,
    logo: LOGO_URL,
  };
}

function orgDescription(lang: AppLang): string {
  return pickOfferSite(lang).seoHome.description;
}

function orgSlogan(lang: AppLang): string {
  return pickOfferSite(lang).hero.chips.slice(0, 3).join(" · ");
}

export function buildOrganizationSchema(lang: AppLang = "ru") {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "IT-24.pro",
    url: `${SITE_ORIGIN}/`,
    logo: LOGO_URL,
    description: orgDescription(lang),
    slogan: orgSlogan(lang),
    foundingDate: String(SEO_TRUST.foundingYear),
    knowsAbout: [
      "Crypto infrastructure",
      "PCI DSS",
      "DDoS mitigation",
      "Kubernetes",
      "Fintech DevOps",
    ],
    sameAs: ["https://t.me/devops_it24"],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+380-63-144-24-24",
      contactType: "customer service",
      availableLanguage: ["Russian", "Ukrainian", "Kazakh", "Uzbek", "Kyrgyz"],
      areaServed: ["UA", "RU", "KZ", "UZ", "BY", "KG"],
    },
  };
}

/** WebSite + SearchAction для sitelinks search box. */
export function buildWebSiteSchema(lang: AppLang) {
  const description = pickOfferSite(lang).seoHome.description;

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    url: `${SITE_ORIGIN}/`,
    name: "IT-24.pro",
    description,
    publisher: orgProviderRef(),
    inLanguage: BCP47_BY_LANG[lang],
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_ORIGIN}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** Service для /devops-outsourcing (головна + субсторінка). */
export function buildDevOpsServiceSchema(lang: AppLang) {
  const name =
    lang === "uk" ? "DevOps-аутсорсинг" : lang === "ky" ? "DevOps-аутсорсинг" : "DevOps-аутсорсинг";
  const description =
    lang === "uk"
      ? `${SEO_TRUST.yearsExperience}+ років на ринку, реакція від ${SEO_TRUST.slaMinutes} хв на Enterprise`
      : lang === "ky"
        ? `${SEO_TRUST.yearsExperience}+ жыл тажрыйба, Enterprise SLA ${SEO_TRUST.slaMinutes} мүн`
        : `${SEO_TRUST.yearsExperience}+ лет на рынке, реакция от ${SEO_TRUST.slaMinutes} мин на Enterprise`;
  const catalogName = lang === "uk" ? "DevOps тарифи" : lang === "ky" ? "DevOps тарифтер" : "DevOps тарифы";
  const pagePath = "/devops-outsourcing";

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_ORIGIN}${pagePath}#service`,
    name,
    serviceType: "DevOps Outsourcing",
    description,
    url: canonicalUrl(localizedPath(pagePath, lang)),
    provider: orgProviderRef(),
    areaServed: {
      "@type": "Country",
      name: "Ukraine",
    },
    offers: {
      "@type": "Offer",
      priceRange: "$1500-$5000",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: catalogName,
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "Starter DevOps" },
          description: "SLA 4 hours, 30 hours/month",
          priceSpecification: {
            "@type": "PriceSpecification",
            price: "1500",
            priceCurrency: "USD",
            unitText: "MONTH",
          },
        },
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "Growth DevOps" },
          description: "SLA 1 hour, 80 hours/month",
          priceSpecification: {
            "@type": "PriceSpecification",
            price: "3000",
            priceCurrency: "USD",
            unitText: "MONTH",
          },
        },
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "Enterprise DevOps" },
          description: `SLA ${SEO_TRUST.slaMinutes} minutes, 24/7 on-call, 160+ hours/month`,
          priceSpecification: {
            "@type": "PriceSpecification",
            price: "5000",
            priceCurrency: "USD",
            unitText: "MONTH",
          },
        },
      ],
    },
  };
}

/** Service для /services/kubernetes-setup. */
export function buildKubernetesServiceSchema(lang: AppLang) {
  const meta = pickPageSeo("kubernetes", lang);
  const pagePath = "/services/kubernetes-setup";
  const name =
    lang === "uk"
      ? "Налаштування Kubernetes під ключ"
      : lang === "ky"
        ? "Kubernetes орнотуу под ключ"
        : "Настройка Kubernetes под ключ";

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_ORIGIN}${pagePath}#service`,
    name,
    serviceType: "Kubernetes Setup and Management",
    description: meta.description,
    url: canonicalUrl(localizedPath(pagePath, lang)),
    provider: orgProviderRef(),
    areaServed: {
      "@type": "Country",
      name: "Ukraine",
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: "3000",
      highPrice: "7000",
      offerCount: "3",
    },
  };
}

/** Service для /services/ci-cd-implementation. */
export function buildCiCdServiceSchema(lang: AppLang) {
  const meta = pickPageSeo("cicd", lang);
  const pagePath = "/services/ci-cd-implementation";
  const name =
    lang === "uk"
      ? "Впровадження CI/CD pipeline"
      : lang === "ky"
        ? "CI/CD pipeline киргизүү"
        : "Внедрение CI/CD pipeline";

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_ORIGIN}${pagePath}#service`,
    name,
    serviceType: "CI/CD Implementation",
    description: meta.description,
    url: canonicalUrl(localizedPath(pagePath, lang)),
    provider: orgProviderRef(),
    areaServed: {
      "@type": "Country",
      name: "Ukraine",
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: "1500",
      highPrice: "5000",
      offerCount: "3",
    },
  };
}

/** Service для /services/ddos-protection. */
export function buildDdosServiceSchema(lang: AppLang) {
  const meta = pickPageSeo("ddos", lang);
  const pagePath = "/services/ddos-protection";
  const name =
    lang === "uk"
      ? "Захист від DDoS атак"
      : lang === "ky"
        ? "DDoS коргоо"
        : "Защита от DDoS атак";

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_ORIGIN}${pagePath}#service`,
    name,
    serviceType: "DDoS Protection",
    description: meta.description,
    url: canonicalUrl(localizedPath(pagePath, lang)),
    provider: orgProviderRef(),
    areaServed: {
      "@type": "Country",
      name: "Ukraine",
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: "2000",
      highPrice: "5000",
      offerCount: "2",
    },
  };
}

/** Service для /services/llmops-private-ai. */
export function buildLlmopsServiceSchema(lang: AppLang) {
  const meta = pickPageSeo("llmops", lang);
  const pagePath = "/services/llmops-private-ai";
  const name =
    lang === "uk"
      ? "LLMOps і приватний AI в Kubernetes"
      : lang === "ky"
        ? "LLMOps жана жеке AI Kubernetes'tе"
        : "LLMOps и приватный AI в Kubernetes";

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_ORIGIN}${pagePath}#service`,
    name,
    serviceType: "LLMOps and Private AI Infrastructure",
    description: meta.description,
    url: canonicalUrl(localizedPath(pagePath, lang)),
    provider: orgProviderRef(),
    areaServed: {
      "@type": "Country",
      name: "Ukraine",
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: "3000",
      highPrice: "15000",
      offerCount: "3",
    },
  };
}

/** Service для /administrirovanie-serverov. */
export function buildServerAdminServiceSchema(lang: AppLang) {
  const meta = pickPageSeo("serverAdmin", lang);
  const pagePath = "/administrirovanie-serverov";
  const name =
    lang === "uk"
      ? "Адміністрування серверів Linux"
      : lang === "ky"
        ? "Linux серверлерди башкаруу"
        : "Администрирование серверов Linux";

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_ORIGIN}${pagePath}#service`,
    name,
    serviceType: "Server Administration",
    description: meta.description,
    url: canonicalUrl(localizedPath(pagePath, lang)),
    provider: orgProviderRef(),
    areaServed: {
      "@type": "Country",
      name: "Ukraine",
    },
  };
}

/** LocalBusiness + ProfessionalService для довіри та локального SEO. */
export function buildLocalBusinessSchema(lang: AppLang) {
  const description = pickOfferSite(lang).seoHome.description;

  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": `${SITE_ORIGIN}/#localbusiness`,
    name: "IT-24.pro",
    description,
    url: `${SITE_ORIGIN}/`,
    telephone: "+380-63-144-24-24",
    email: "admin@it-24.pro",
    image: OG_IMAGE_URL,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Konin",
      addressRegion: "Greater Poland",
      postalCode: "62-510",
      addressCountry: "PL",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
    priceRange: "$$",
    parentOrganization: orgProviderRef(),
  };
}

const FAQ_SCHEMA_RU = [
  {
    question: "Чем занимается IT-24.pro?",
    answer:
      "Инфраструктура для криптовалютного сектора, финтеха и highload: инциденты и DDoS, аудит и готовность к PCI DSS, retainer $1 500–2 500/мес, проекты под ключ $3–8k. Работаю лично, ответы в Telegram.",
  },
  {
    question: "Сколько стоят услуги?",
    answer:
      "Неотложная помощь $100–150/ч (пакет от $600), аудит $300–600, retainer $1 500–2 500/мес, проекты под ключ $3 000–8 000 fixed.",
  },
  {
    question: "Как оплатить?",
    answer: "USDT (TRC-20), UAH, Wise/Payoneer или польский инвойс с VAT. Юридическое лицо в Польше.",
  },
  {
    question: "Работаете ли с клиентами вне Украины?",
    answer:
      "Да: страны СНГ и удалённо. Основной канал — Telegram. Рынок Европейского союза — отдельно на nexops.eu.",
  },
] as const;

const FAQ_SCHEMA_UK = [
  {
    question: "Чим займається IT-24.pro?",
    answer:
      "Інфраструктура для криптовалютного сектору, фінтеху та highload: інциденти та DDoS, аудит і PCI DSS, retainer $1 500–2 500/міс, проєкти під ключ $3–8k. Працюю особисто, відповіді в Telegram.",
  },
  {
    question: "Скільки коштують послуги?",
    answer:
      "Невідкладна допомога $100–150/год (пакет від $600), аудит $300–600, retainer $1 500–2 500/міс, проєкти $3 000–8 000 fixed.",
  },
  {
    question: "Як оплатити?",
    answer: "USDT (TRC-20), UAH, Wise/Payoneer або польський інвойс з VAT. Юридична особа в Польщі.",
  },
  {
    question: "Працюєте з клієнтами поза Україною?",
    answer: "Так: країни СНД і віддалено. Канал — Telegram. Ринок ЄС — nexops.eu.",
  },
] as const;

const FAQ_SCHEMA_KY = [
  {
    question: "IT-24.pro эмне кылат?",
    answer:
      "Криптовалюта сектору, финтех жана highload инфраструктурасы: инцидент жана DDoS, аудит, retainer $1 500–2 500/ай, долбоорлор $3–8k. Өзүм иштейм, Telegram.",
  },
  {
    question: "Кызматтар канча турат?",
    answer:
      "Шашылыш жардам $100–150/саат, аудит $300–600, retainer $1 500–2 500/ай, долбоорлор $3 000–8 000.",
  },
  {
    question: "Кантип төлөсө болот?",
    answer: "USDT, UAH, Wise/Payoneer же Польша инвойс (VAT). Юридикалык жак Польшада.",
  },
  {
    question: "Украинадан тышкары иштейсизби?",
    answer: "Ооба, КМШ жана удалённо. Telegram. Европа Биримдиги — nexops.eu.",
  },
] as const;

const FAQ_SCHEMA_KK = [
  {
    question: "IT-24.pro не істейді?",
    answer:
      "Төлем, айырбас және highload инфрақұрылымы: инцидент пен DDoS, аудит және PCI DSS, retainer $1 500–2 500/ай, жобалар $3–8k. Өзім жауап беремін — Telegram.",
  },
  {
    question: "Қызмет қанша тұрады?",
    answer:
      "Шұғыл көмек $100–150/сағ (пакет $600-нан), аудит $300–600, retainer $1 500–2 500/ай, жобалар $3 000–8 000 fixed.",
  },
  {
    question: "Қалай төлеуге болады?",
    answer: "USDT (TRC-20), UAH, Wise/Payoneer немесе Польша инвойсы (VAT). Заңды тұлға Польшада.",
  },
  {
    question: "Қазақстаннан тыс жұмыс істейсіз бе?",
    answer: "Иә: ТМД және қашықтан. Негізгі арна — Telegram. ЕО нарығы — nexops.eu.",
  },
] as const;

const FAQ_SCHEMA_UZ = [
  {
    question: "IT-24.pro nima qiladi?",
    answer:
      "Ayirboshlash, to‘lov va highload infratuzilmasi: hodisa va DDoS, audit va PCI DSS, retainer $1 500–2 500/oy, loyihalar $3–8k. O‘zim javob beraman — Telegram.",
  },
  {
    question: "Xizmatlar qancha turadi?",
    answer:
      "Shoshilinch yordam $100–150/soat (paket $600 dan), audit $300–600, retainer $1 500–2 500/oy, loyihalar $3 000–8 000 fixed.",
  },
  {
    question: "Qanday to‘lash mumkin?",
    answer: "USDT (TRC-20), UAH, Wise/Payoneer yoki Polsha invoysi (VAT). Yuridik shaxs Polshada.",
  },
  {
    question: "O‘zbekistondan tashqarida ishlaysizmi?",
    answer: "Ha: MDH va masofadan. Asosiy kanal — Telegram. Yevropa bozori — nexops.eu.",
  },
] as const;

const FAQ_BY_LANG = {
  ru: FAQ_SCHEMA_RU,
  uk: FAQ_SCHEMA_UK,
  kk: FAQ_SCHEMA_KK,
  uz: FAQ_SCHEMA_UZ,
  ky: FAQ_SCHEMA_KY,
} as const;

export function buildFaqPageSchema(lang: AppLang) {
  const items = FAQ_BY_LANG[lang] ?? FAQ_SCHEMA_RU;
  return buildFaqPageSchemaFromItems(
    items.map((item) => ({ q: item.question, a: item.answer })),
  );
}

export function buildFaqPageSchemaFromItems(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

export function buildSubServiceSchema(opts: {
  name: string;
  description: string;
  url: string;
  priceMin?: number;
  priceMax?: number;
}) {
  const offer =
    opts.priceMin != null && opts.priceMax != null
      ? {
          "@type": "AggregateOffer",
          lowPrice: String(opts.priceMin),
          highPrice: String(opts.priceMax),
          priceCurrency: "USD",
        }
      : undefined;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    serviceType: opts.name,
    provider: {
      "@type": "Organization",
      name: "IT-24.pro",
      url: `${SITE_ORIGIN}/`,
      logo: LOGO_URL,
    },
    areaServed: ["UA", "EU", "KG", "KZ", "BY"],
    ...(offer ? { offers: offer } : {}),
  };
}

/**
 * JSON-LD для головної: Organization, WebSite, LocalBusiness, FAQPage.
 * Service-схеми — на відповідних субсторінках; BreadcrumbList — buildBreadcrumbListSchema().
 */
export function buildHomePageSchemas(lang: AppLang) {
  return [buildOrganizationSchema(lang), buildWebSiteSchema(lang), buildLocalBusinessSchema(lang)];
}

export { OG_IMAGE_URL };
